"use client";

import { useEffect, useState } from "react";
import * as THREE from "three";
import { parseToSrgb } from "@/lib/design/oklch";

/**
 * Maps each viewer scene role to the CSS token that defines it. IB-462: the
 * canvas has its own `--color-viewer-background`, offset from the app background
 * so the 3D stage reads as a distinct layer from the surrounding panels rather
 * than blending into them; the rest are tuned for legibility against it (see
 * theme.css and theme-colors.test.ts).
 */
const VIEWER_COLOR_VARS = {
  background: "--color-viewer-background",
  grid: "--color-viewer-grid",
  axisX: "--color-viewer-axis-x",
  axisY: "--color-viewer-axis-y",
  axisZ: "--color-viewer-axis-z",
  label: "--color-viewer-label",
  wireframe: "--color-viewer-wireframe",
} as const;

export type ThemeViewerColors = Record<keyof typeof VIEWER_COLOR_VARS, THREE.Color>;

let rasterCtx: CanvasRenderingContext2D | null | undefined;

/**
 * Normalize any CSS color string to sRGB [0,1] by painting it on a 1x1 canvas and
 * reading the pixel back. The browser understands every color space it renders —
 * critically `lab()`, which the production CSS build (Lightning CSS) emits when it
 * rewrites our `oklch()` tokens, and which no hand-rolled parser here covers.
 * Returns null when a 2D context isn't available (jsdom) so callers can fall back.
 */
function rasterizeToSrgb(css: string): { r: number; g: number; b: number } | null {
  if (rasterCtx === undefined) {
    try {
      rasterCtx = document.createElement("canvas").getContext("2d", { willReadFrequently: true });
    } catch {
      // jsdom throws "Not implemented" for getContext; fall back to string parsing.
      rasterCtx = null;
    }
  }
  if (!rasterCtx) return null;
  // Assign a known color first: an invalid `css` leaves fillStyle at the sentinel
  // rather than painting a stale value, so we can tell parsing failed.
  rasterCtx.fillStyle = "#000000";
  rasterCtx.fillStyle = css;
  rasterCtx.fillRect(0, 0, 1, 1);
  const [r, g, b] = rasterCtx.getImageData(0, 0, 1, 1).data;
  return { r: r / 255, g: g / 255, b: b / 255 };
}

/**
 * Resolve a computed CSS color string to a THREE.Color. `getComputedStyle` returns
 * the color in whatever space the browser serializes to — `rgb()` for hsl tokens,
 * but `lab()` for our oklch tokens once the prod build rewrites them. Rasterize
 * through a canvas so any format is handled uniformly; fall back to parseToSrgb
 * (oklch/hsl/rgb/hex) only when no canvas exists (jsdom tests). Feed sRGB so THREE
 * does the sRGB→working conversion, matching how setStyle treats literals elsewhere.
 */
export function cssColorToThree(css: string): THREE.Color {
  let rgb = rasterizeToSrgb(css);
  if (!rgb) {
    try {
      rgb = parseToSrgb(css);
    } catch {
      // Unresolved var() (jsdom returns the literal "var(--x)") or an unparseable
      // value — default rather than crash the viewer. Real browsers rasterize above.
      return new THREE.Color();
    }
  }
  return new THREE.Color().setRGB(rgb.r, rgb.g, rgb.b, THREE.SRGBColorSpace);
}

/** Resolve each token through a probe element (respects cascade + var() indirection). */
function readViewerColors(): ThemeViewerColors {
  const probe = document.createElement("span");
  probe.style.display = "none";
  document.body.appendChild(probe);
  const colors = {} as ThemeViewerColors;
  for (const key of Object.keys(VIEWER_COLOR_VARS) as Array<keyof typeof VIEWER_COLOR_VARS>) {
    probe.style.color = `var(${VIEWER_COLOR_VARS[key]})`;
    colors[key] = cssColorToThree(getComputedStyle(probe).color);
  }
  probe.remove();
  return colors;
}

/**
 * Theme-aware 3D viewer colors. Returns `null` until mounted (so SSR and the
 * first paint fall back to the viewer defaults), then recomputes whenever the
 * theme changes so the canvas recolors live on a Light/Dark toggle.
 *
 * We key off the actual `.dark`/`.light` class mutation on `<html>` rather than
 * next-themes' `resolvedTheme`: `resolvedTheme` updates a tick before the class
 * lands on the DOM, so reading CSS custom properties at that point returns the
 * *previous* theme's values (the canvas would only correct on reload). A
 * MutationObserver fires exactly when the new theme's variables are in effect.
 */
export function useThemeViewerColors(): ThemeViewerColors | null {
  // Read synchronously on the first client render to avoid a one-frame flash of
  // the dark fallback background. next-themes applies the theme class to <html>
  // via a pre-hydration script, so the class is already correct here.
  const [colors, setColors] = useState<ThemeViewerColors | null>(() =>
    typeof document === "undefined" ? null : readViewerColors()
  );

  useEffect(() => {
    // Only seed here if the synchronous initializer couldn't run (SSR). When it
    // already read the colors, returning the same reference lets React bail out
    // of a redundant mount render. Live theme toggles are handled by the observer.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setColors((prev) => prev ?? readViewerColors());
    const observer = new MutationObserver(() => setColors(readViewerColors()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  return colors;
}

/**
 * Whether the dark theme is active, tracked via the same `<html>` class mutation
 * as useThemeViewerColors so in-scene shader color-domain uniforms flip live on a
 * Light/Dark toggle without a reload.
 */
export function useThemeIsDark(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document === "undefined" ? true : document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    const read = () => setIsDark(document.documentElement.classList.contains("dark"));
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}
